import { useCallback } from 'react';

import { AxiosError } from 'axios';
import { authState } from 'hooks/auth/util';
import _uniq from 'lodash/uniq';
import { useSnackbar } from 'notistack';
import { QueryKeys } from 'queries';
import { useIntl } from 'react-intl';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useRecoilState } from 'recoil';

import apiService from 'services/api/ApiService';
import { GetAccountResponse } from 'services/api/definitions/account.definitions';

export const useFavorite = (
  options: UseMutationOptions<
    unknown,
    AxiosError,
    { id: string },
    { previousAccount: GetAccountResponse | undefined }
  > = {},
) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [auth, setAuth] = useRecoilState(authState);

  const { mutate: favoriteGame } = useMutation(({ id }) => apiService.addFavoriteGame(id), {
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(QueryKeys.account.all());

      // Snapshot the previous value
      const previousAccount = queryClient.getQueryData<GetAccountResponse>(QueryKeys.account.all());

      // Optimistically update to the new value
      queryClient.setQueryData<GetAccountResponse | undefined>(QueryKeys.account.all(), old => {
        if (!old) {
          return;
        }

        const newAccount = {
          ...old,
          favorites: _uniq([...(old?.favorites ?? []), id]),
        };

        setAuth({ ...auth, user: newAccount });

        return newAccount;
      });
      // Return a context object with the snapshotted value
      return { previousAccount };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (error, _id, context) => {
      // If error is a 409 we handle it as a success. Because api state is correct and the game should be favorited already
      if (error.response?.status === 409) {
        return;
      }

      if (context?.previousAccount) {
        queryClient.setQueryData(QueryKeys.account.all(), context.previousAccount);
        setAuth({ ...auth, user: context.previousAccount });
      }

      enqueueSnackbar(intl.formatMessage({ id: 'games.error.favorite' }), { variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.account.all());
    },
    ...options,
  });

  const { mutate: unFavoriteGame } = useMutation(({ id }) => apiService.deleteFavoriteGame(id), {
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(QueryKeys.account.all());

      // Snapshot the previous value
      const previousAccount = queryClient.getQueryData<GetAccountResponse>(QueryKeys.account.all());

      // Optimistically update to the new value
      queryClient.setQueryData<GetAccountResponse | undefined>(QueryKeys.account.all(), old => {
        if (!old) {
          return;
        }

        const newAccount = {
          ...old,
          favorites: (old?.favorites ?? []).filter(i => i !== id),
        };

        setAuth({ ...auth, user: newAccount });

        return newAccount;
      });
      // Return a context object with the snapshotted value
      return { previousAccount };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (error, _id, context) => {
      // If error is a 404 this means the favorite is not found, so the api was already correct
      if (error.response?.status === 404) {
        return;
      }

      if (context?.previousAccount) {
        queryClient.setQueryData(QueryKeys.account.all(), context.previousAccount);
        setAuth({ ...auth, user: context.previousAccount });
      }

      enqueueSnackbar(intl.formatMessage({ id: 'games.error.unfavorite' }), { variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.account.all());
    },
    ...options,
  });

  const isFavoritedGame = useCallback(
    (gameId: string) => {
      const account = auth.user;
      return (account?.favorites ?? []).includes(gameId);
    },
    [auth.user],
  );

  return {
    favoriteGame,
    unFavoriteGame,
    isFavoritedGame,
  };
};
